/**
 * Adapter: convert ClobMarket + price data → SDK Market type
 * so existing MarketCard / TheMarketPage components render CLOB markets unchanged.
 */
import type { ClobMarket } from './types';
import { FollowStatus, MarketStatus, type Market, type MarketOption } from '@/libs/sdk/api';

/** Price snapshot from /price/:tokenId */
export type ClobPriceSnapshot = { bid: string; ask: string };

/**
 * Build a fake `marketAddress` that routes to /clob/:conditionId.
 * MarketCard uses `navigate(`/market/${market.marketAddress}`)` for APMM;
 * we prefix with `clob:` so the card-list can detect CLOB markets and
 * route to `/clob/:conditionId` instead.
 */
const CLOB_ADDR_PREFIX = 'clob:';

export const isClobMarketAddress = (addr: string) => addr.startsWith(CLOB_ADDR_PREFIX);

export const extractConditionId = (addr: string) =>
  addr.startsWith(CLOB_ADDR_PREFIX) ? addr.slice(CLOB_ADDR_PREFIX.length) : addr;

/**
 * Convert a ClobMarket + optional price snapshot into an SDK Market object
 * suitable for rendering by MarketCard.
 */
export const clobMarketToMarket = (
  cm: ClobMarket,
  price?: ClobPriceSnapshot | null,
): Market => {
  const bid = price?.bid ? Number(price.bid) : 0.5;
  const ask = price?.ask ? Number(price.ask) : 0.5;
  // Clamp to [0,1]
  const yesPrice = Math.max(0, Math.min(1, bid));
  const noPrice = Math.max(0, Math.min(1, 1 - ask));

  // Title from metadata or fallback
  const title = (cm as any).title || `Market ${cm.conditionId.slice(0, 6)}…${cm.conditionId.slice(-4)}`;

  // Build MarketOption[] mimicking APMM options for MarketCard
  const yesOption: MarketOption = {
    decimal: 0, // prices are already 0-1 float
    tokenAddress: cm.tokens?.[0]?.tokenId || cm.conditionId,
    tokenDescription: 'Yes',
    tokenName: 'Yes',
    tokenPicUrl: '',
    tokenPrice: String(Math.round(yesPrice * 100)),
  };
  const noOption: MarketOption = {
    decimal: 0,
    tokenAddress: cm.tokens?.[1]?.tokenId || `${cm.conditionId}-no`,
    tokenDescription: 'No',
    tokenName: 'No',
    tokenPicUrl: '',
    tokenPrice: String(Math.round(noPrice * 100)),
  };

  return {
    marketAddress: `${CLOB_ADDR_PREFIX}${cm.conditionId}`,
    marketName: title,
    categoryName: 'sports',
    marketPicUrl: (cm as any).imageUrl || '',
    marketDescription: (cm as any).description || '',
    marketStatus: cm.status === 'active' ? MarketStatus.progress : MarketStatus.closed,
    marketVolume: '0',
    marketDecimal: '0',
    participantsCount: 0,
    options: [yesOption, noOption],
    isFollowed: FollowStatus.no,
    createdAt: cm.createdAt ? new Date(cm.createdAt).getTime() / 1000 : 0,
    deadline: 0,
    eventId: '',
    result: '',
  };
};
